import React from 'react';
import './DetailsView.css';
import { CloseRow } from './details/CloseRow';
import { DeetBox } from './details/DeetBox';
import { DeetList } from './details/DeetList';
import { DeetEmpty } from './details/DeetEmpty';
import Event from '../models/Event';
import { eventApi } from '../api/EventApi';
import _ from 'lodash';
import { collectionApi } from '../api/CollectionApi';
import Collection from '../models/Collection';
import Logger from '../util/Logger';
import { ideaApi } from '../api/IdeaApi';
import { PromiseBatcher } from 'promise-batching-queue';
import { DeetLabel } from './details/DeetLabel';
import { DeetTop } from './details/DeetTop';
import { EventQuestionType } from '../models/EventQuestionType';
import LoadingAndPulse from '../components/shared/LoadingAndPulse';

const eventQueue = PromiseBatcher.newEagerQueue(8);

export class DetailsView extends React.Component {

  state = {
    loading: false
  }

  getEvent = (eventId) => {
    const { eventCache } = this.props;
    return eventCache[eventId];
  }

  getEventDetailsAndStats = (eventId) => {
    Logger.info('Getting event details: ', eventId);
    return eventApi.getDetails(eventId).then(event => {
      return eventApi.getStats(Event.getId(event)).then(stats => {
        return { event, eventStats: stats };
      });
    })
  };

  getCollectionDetailsAndStats = (eventPayload, existingCollection) => {
    if (existingCollection) {
      Logger.info('Existing collection for event: ', Event.getId(eventPayload.event));
      return {
        event: eventPayload.event,
        eventStats: eventPayload.eventStats,
        collection: existingCollection.collection,
        collectionStats: existingCollection.collectionStats
      };
    }

    if (!!_.get(eventPayload.event, 'collection.id')) {
      const collectionId = Collection.getId(Event.getCollection(eventPayload.event));
      Logger.info('Getting collection details: ', collectionId);
      return collectionApi.getDetails(collectionId)
        .then(collection => {
          return collectionApi.getStats(Collection.getId(collection))
            .then(collectionStats => {
              return {
                event: eventPayload.event,
                eventStats: eventPayload.eventStats,
                collection: collection,
                collectionStats: collectionStats
              };
            });
        });
    } else {
      Logger.info('No collection for event: ', Event.getId(eventPayload.event));
      return {
        event: eventPayload.event,
        eventStats: eventPayload.eventStats,
        collection: null,
        collectionStats: null
      };
    }
  }

  getIdeas = (payload) => {
    const eventId = Event.getId(payload.event);
    Logger.info('Get ideas for event: ', eventId)
    return ideaApi.getIdeas(eventId).then(ideas => {
      return {
        event: payload.event,
        eventStats: payload.eventStats,
        collection: payload.collection,
        collectionStats: payload.collectionStats,
        // Add child_count field to idea object so it plays nice with existing API
        ideas: ideas.map(i => {
          i.child_count = i.comment_count;
          return i;
        })
      };
    });
  };

  updateEventCache = (payload) => {
    const { eventCache, handleUpdateEventCache } = this.props;
    const eventId = Event.getId(payload.event);
    const cached = this.getEvent(eventId);
    if (!!cached) {
      return Promise.resolve(cached);
    } else {
      eventCache[eventId] = payload;
      return new Promise((resolve) => {
        handleUpdateEventCache(eventCache, () => {
          resolve(payload);
        });
      });
    }
  }

  recursivelyFetchOtherEventsInCollection = (payload, recurring) => {
    if (!!payload.collection) {
      const events = Collection.getEvents(payload.collection);
      return events.map(e => {
        return eventQueue.queue(() => {
          const id = Event.getId(e);
          if (!this.getEvent(id) && !recurring) {
            Logger.info('Recursively fetch other event: ', id);
            return this.fetchAndCacheEventDetails(id, true, {
              collection: payload.collection,
              collectionStats: payload.collectionStats
            });
          } else {
            Logger.info('Do not enter recursion for event:', id);
            return Promise.resolve();
          }
        }).catch(error => {
          Logger.error('Error queueing up recursive promise: ', error);
          throw error;
        });
      });
    } else {
      return payload;
    }
  }

  fetchAndCacheEventDetails = (eventId, recurring, existingCollection) => {
    return this.getEventDetailsAndStats(eventId)
      .then(payload => this.getCollectionDetailsAndStats(payload, existingCollection))
      .then(this.getIdeas)
      .then(this.updateEventCache)
      .then(payload => this.recursivelyFetchOtherEventsInCollection(payload, recurring))
      .catch(error => {
        const { eventCache, handleUpdateEventCache } = this.props;
        eventCache[eventId] = null;
        handleUpdateEventCache(eventCache);
        throw error;
      });
  }

  fetchEventIfNeeded (prevProps) {
    const { event } = this.props;
    if (!event) {
      return;
    }

    let prevId;
    if (prevProps) {
      const { event } = prevProps;
      if (event) {
        prevId = Event.getId(event);
      } else {
        prevId = null;
      }
    } else {
      prevId = null;
    }

    const eventId = Event.getId(event);
    if (eventId !== prevId) {

      if (!!event.sample) {
        this.updateEventCache({
          event: event,
          eventStats: event.sample.stats,
          collection: event.sample.collection,
          collectionStats: event.sample.collectionStats,
          ideas: event.sample.ideas
        });
        return;
      }

      const cachedEvent = this.getEvent(eventId);
      if (!cachedEvent) {
        this.setState({ loading: true}, () => {
          this.fetchAndCacheEventDetails(eventId, false, null)
            .then(() => this.setState({ loading: false }))
            .catch(error => this.setState({ loading: false }, () => {
              Logger.error('Error fetching event details into cache: ', error);
              // TODO handle
            }));
        });
      }
    }
  }

  componentDidUpdate (prevProps) {
    this.fetchEventIfNeeded(prevProps);
  }

  render () {
    const { loading } = this.state;
    const { event, isDesktop, handleClose } = this.props;

    // If there is no event, don't render this section on mobile
    let loadedEvent;
    if (!event) {
      loadedEvent = null;
    } else {
      const eventId = Event.getId(event);
      loadedEvent = this.getEvent(eventId);
    }

    if (!loading && !loadedEvent && !isDesktop) {
      return null;
    }

    let renderMe;
    if (!loadedEvent) {
      renderMe = null;
    } else {
      if (!loadedEvent.collection) {
        renderMe = this.eventDeets(loadedEvent, 0);
      } else {
        renderMe = Collection.getEvents(loadedEvent.collection)
          .map((e, i) => {
            const cached = this.getEvent(Event.getId(e));
            // We may not have this entry cached at this point but it will come back later. I hope.
            if (!cached) {
              return null;
            } else {
              return this.eventDeets(cached, i);
            }
          });
      }
    }

    const { handleGoClick, handleEditClick, handlePresentClick, handleShareClick, handleInviteClick } = this.props;
    return (
      <div className={ `col no-gutters details-view ${isDesktop ? 'details-view-desktop allow-scrolling' : 'details-view-mobile'}`}>
        { (!isDesktop && !!loadedEvent) && <CloseRow handleClose={ handleClose } /> }
        { !loadedEvent && <EmptyState /> }
        { !!loading && <Loading /> }
        { !!loadedEvent && (
          <DeetTop
            isDesktop={ isDesktop }
            event={ loadedEvent.event }
            handleEditClick={ handleEditClick }
            handleShareClick={ handleShareClick }
            handleInviteClick={ handleInviteClick }
            handlePresentClick={ handlePresentClick }
            handleGoClick={ handleGoClick } />
        )}
        <div className={ `col no-gutters deet-scrolls ${isDesktop ? '' : ''}`}>
          { renderMe }
        </div>
      </div>
    );
  }

  isEventEmpty = (event, ideas) => {
    const type = Event.getQuestionType(event);
    if (EventQuestionType.isOpenEndedType(type)) {
      return !ideas || ideas.length <= 0;
    } else {
      return !ideas || ideas.length <= 0 || !ideas.some(i => i.choice_count > 0);
    }
  };

  eventDeets = ({ event, eventStats, ideas }, index) => {
    if (!event || !eventStats || !ideas) {
      return null;
    }

    const { isDesktop, handleGoClick, handleShareClick } = this.props;
    return (
      <div className={ `card m-4 pt-3 px-0 shadow-sm ${isDesktop ? 'deet-section-desktop' : 'deet-section-mobile'}` } key={ Event.getId(event) }>
        <div className="card-body p-4">
          { !event.sample && (
          <h5 className="card-subtitle">Question { index + 1 }</h5>
          )}
          <DeetLabel event={ event } isDesktop={ isDesktop } handleGoClick={ handleGoClick } />
          { this.isEventEmpty(event, ideas)
          ? (
            <React.Fragment>
              <DeetEmpty event={ event } eventStats={ eventStats } isDesktop={ isDesktop } ideas={ ideas } handleShareClick={ handleShareClick } />
            </React.Fragment>
          )
          : (
            <React.Fragment>
              <DeetBox event={ event } eventStats={ eventStats } isDesktop={ isDesktop } />
              <DeetList event={ event } eventStats={ eventStats } ideas={ ideas } isDesktop={ isDesktop } />
            </React.Fragment>
          ) }
        </div>
      </div>
    );
  }
}

const EmptyState = () => {
  return (
    <div className="row no-gutters m-auto white-af"></div>
  );
};

const Loading = () => {
  return (
    <div className="row no-gutters m-auto white-af">
      <LoadingAndPulse
        size={ 20 }
        color="#3D9AE2"
        whatIsLoading="POPin"
         />
    </div>
  );
};